import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import AdminNav from '../components/AdminNav';
import { formatInrWhole } from '../utils/money';
import { whatsappUrl } from '../utils/whatsapp';
import './AdminBilling.css';

function WhatsAppIcon() {
  return (
    <svg className="bill-wa-icon" viewBox="0 0 24 24" width="20" height="20" aria-hidden>
      <path
        fill="currentColor"
        d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.435 9.884-9.881 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"
      />
    </svg>
  );
}

function fmtYmd(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function listRange(period, customFrom, customTo) {
  const now = new Date();
  if (period === 'this_month') {
    const from = new Date(now.getFullYear(), now.getMonth(), 1);
    const to = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    return { from: fmtYmd(from), to: fmtYmd(to) };
  }
  if (period === 'last_month') {
    const from = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    const to = new Date(now.getFullYear(), now.getMonth(), 0);
    return { from: fmtYmd(from), to: fmtYmd(to) };
  }
  if (period === 'custom' && customFrom && customTo) {
    return { from: customFrom, to: customTo };
  }
  return { from: '', to: '' };
}

async function downloadBillPdf(id, billNumber) {
  const res = await fetch(`/api/bills/${id}/pdf`, { credentials: 'include' });
  if (!res.ok) return;
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${String(billNumber || 'invoice').replace(/[^a-zA-Z0-9-_]/g, '_')}.pdf`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

export default function AdminBilling() {
  const { admin, loading: authLoading } = useAuth();
  const [tab, setTab] = useState('new');

  const [shopName, setShopName] = useState('');
  const [shopAddress, setShopAddress] = useState('');
  const [shopGstin, setShopGstin] = useState('');
  const [shopPhone, setShopPhone] = useState('');
  const [defaultGst, setDefaultGst] = useState('3');
  const [hasLogo, setHasLogo] = useState(false);
  const [logoFile, setLogoFile] = useState(null);
  const [clearLogo, setClearLogo] = useState(false);
  const [settingsLoading, setSettingsLoading] = useState(true);
  const [settingsSaving, setSettingsSaving] = useState(false);
  const [logoBust, setLogoBust] = useState(0);

  const [jewels, setJewels] = useState([]);
  const [jewelsLoading, setJewelsLoading] = useState(false);
  const [jewelQuery, setJewelQuery] = useState('');
  const [quantities, setQuantities] = useState({});
  const [customerName, setCustomerName] = useState('');
  const [customerAddress, setCustomerAddress] = useState('');
  const [customerPhone, setCustomerPhone] = useState('');
  const [gstPercent, setGstPercent] = useState('3');
  const [markSold, setMarkSold] = useState(false);
  const [billSubmitting, setBillSubmitting] = useState(false);
  const [billError, setBillError] = useState('');
  const [lastBill, setLastBill] = useState(null);

  const [bills, setBills] = useState([]);
  const [listTotal, setListTotal] = useState(0);
  const [listPage, setListPage] = useState(1);
  const [listLoading, setListLoading] = useState(false);
  const [period, setPeriod] = useState('this_month');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');
  const [filterPhone, setFilterPhone] = useState('');
  const [filterName, setFilterName] = useState('');
  const [stats, setStats] = useState({ bill_count: 0, total_sales: 0 });

  const loadSettings = useCallback(async () => {
    setSettingsLoading(true);
    try {
      const res = await fetch('/api/invoice-settings', { credentials: 'include' });
      if (!res.ok) throw new Error('Failed');
      const d = await res.json();
      setShopName(d.shop_name || '');
      setShopAddress(d.address || '');
      setShopGstin(d.gstin || '');
      setShopPhone(d.shop_phone || '');
      setDefaultGst(String(d.default_gst_percent ?? 3));
      setHasLogo(!!d.has_logo);
      setClearLogo(false);
      setLogoFile(null);
      setGstPercent(String(d.default_gst_percent ?? 3));
    } catch {
      setShopName('Vikky Gold Palace');
    } finally {
      setSettingsLoading(false);
    }
  }, []);

  const loadJewels = useCallback(async () => {
    setJewelsLoading(true);
    try {
      const res = await fetch('/api/jewels', { credentials: 'include' });
      if (!res.ok) throw new Error('Failed');
      setJewels(await res.json());
    } catch {
      setJewels([]);
    } finally {
      setJewelsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  useEffect(() => {
    if (tab === 'new') loadJewels();
  }, [tab, loadJewels]);

  const fetchTransactions = useCallback(async () => {
    if (period === 'custom' && (!customFrom || !customTo)) {
      setBills([]);
      setListTotal(0);
      setListLoading(false);
      return;
    }
    setListLoading(true);
    try {
      const { from, to } = listRange(period, customFrom, customTo);
      const q = new URLSearchParams({ page: String(listPage), limit: '15' });
      if (from) q.set('from', from);
      if (to) q.set('to', to);
      if (filterPhone.trim()) q.set('phone', filterPhone.trim());
      if (filterName.trim()) q.set('q', filterName.trim());
      const res = await fetch(`/api/bills?${q}`, { credentials: 'include' });
      if (!res.ok) return;
      const d = await res.json();
      setBills(d.bills);
      setListTotal(d.total);
    } catch {
      setBills([]);
    } finally {
      setListLoading(false);
    }
  }, [period, customFrom, customTo, listPage, filterPhone, filterName]);

  const fetchStats = useCallback(async () => {
    if (period === 'custom' && (!customFrom || !customTo)) {
      setStats({ bill_count: 0, total_sales: 0 });
      return;
    }
    try {
      let url = '/api/bills/stats?period=this_month';
      if (period === 'last_month') url = '/api/bills/stats?period=last_month';
      if (period === 'custom' && customFrom && customTo) {
        url = `/api/bills/stats?period=custom&from=${encodeURIComponent(customFrom)}&to=${encodeURIComponent(customTo)}`;
      }
      const res = await fetch(url, { credentials: 'include' });
      if (!res.ok) return;
      const d = await res.json();
      setStats({ bill_count: d.bill_count, total_sales: d.total_sales });
    } catch {
      setStats({ bill_count: 0, total_sales: 0 });
    }
  }, [period, customFrom, customTo]);

  useEffect(() => {
    if (tab !== 'transactions') return;
    fetchStats();
  }, [tab, fetchStats]);

  useEffect(() => {
    if (tab !== 'transactions') return;
    fetchTransactions();
  }, [tab, fetchTransactions]);

  const availableJewels = useMemo(() => {
    const q = jewelQuery.trim().toLowerCase();
    return jewels.filter((j) => {
      if (j.is_sold) return false;
      if (!q) return true;
      return (
        String(j.name || '')
          .toLowerCase()
          .includes(q) || String(j.id).includes(q)
      );
    });
  }, [jewels, jewelQuery]);

  const lineEntries = useMemo(() => Object.entries(quantities).filter(([, q]) => q > 0), [quantities]);

  const previewTotals = useMemo(() => {
    let sub = 0;
    for (const [idStr, qty] of lineEntries) {
      const j = jewels.find((x) => String(x.id) === idStr);
      if (!j) continue;
      const unit =
        j.display_price != null && !Number.isNaN(Number(j.display_price))
          ? Math.round(Number(j.display_price))
          : 0;
      sub += unit * qty;
    }
    const gstPct = parseFloat(gstPercent, 10);
    const gst = Number.isNaN(gstPct) ? 0 : Math.round((sub * gstPct) / 100);
    return { subtotal: sub, gst, grand: sub + gst };
  }, [lineEntries, jewels, gstPercent]);

  const addJewel = (id) => {
    setQuantities((prev) => ({
      ...prev,
      [id]: (prev[id] || 0) + 1,
    }));
  };

  const setQty = (id, v) => {
    const n = parseInt(v, 10);
    if (Number.isNaN(n) || n < 1) {
      setQuantities((prev) => {
        const next = { ...prev };
        delete next[id];
        return next;
      });
      return;
    }
    setQuantities((prev) => ({ ...prev, [id]: n }));
  };

  const removeLine = (id) => {
    setQuantities((prev) => {
      const next = { ...prev };
      delete next[id];
      return next;
    });
  };

  const saveSettings = async (e) => {
    e.preventDefault();
    setSettingsSaving(true);
    try {
      const fd = new FormData();
      fd.append('shop_name', shopName);
      fd.append('address', shopAddress);
      fd.append('gstin', shopGstin);
      fd.append('shop_phone', shopPhone);
      fd.append('default_gst_percent', defaultGst);
      if (clearLogo) fd.append('clear_logo', 'true');
      if (logoFile) fd.append('logo', logoFile);
      const res = await fetch('/api/invoice-settings', {
        method: 'PUT',
        credentials: 'include',
        body: fd,
      });
      const d = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(d.error || 'Save failed');
      setHasLogo(!!d.has_logo);
      setLogoFile(null);
      setClearLogo(false);
      setLogoBust(Date.now());
      setDefaultGst(String(d.default_gst_percent));
      setGstPercent(String(d.default_gst_percent));
    } catch (err) {
      alert(err.message);
    } finally {
      setSettingsSaving(false);
    }
  };

  const submitBill = async (e) => {
    e.preventDefault();
    setBillError('');
    if (lineEntries.length === 0) {
      setBillError('Add at least one product.');
      return;
    }
    setBillSubmitting(true);
    try {
      const items = lineEntries.map(([jewel_id, quantity]) => ({
        jewel_id: parseInt(jewel_id, 10),
        quantity,
      }));
      const res = await fetch('/api/bills', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          customer_name: customerName,
          customer_address: customerAddress,
          customer_phone: customerPhone,
          gst_percent: gstPercent === '' ? undefined : parseFloat(gstPercent, 10),
          mark_sold: markSold,
          items,
        }),
      });
      const d = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(d.error || 'Failed to create bill');
      setLastBill(d);
      setQuantities({});
      setCustomerName('');
      setCustomerAddress('');
      setCustomerPhone('');
      setMarkSold(false);
      loadJewels();
    } catch (err) {
      setBillError(err.message);
    } finally {
      setBillSubmitting(false);
    }
  };

  const totalPages = Math.ceil(listTotal / 15);

  if (authLoading) return <div className="page-center"><p>Loading...</p></div>;
  if (!admin) return <Navigate to="/admin/login" replace />;

  const waText = lastBill
    ? `Hi ${lastBill.customer_name}, thank you for your purchase from Vikky Gold Palace. Your bill ${lastBill.bill_number} total is ${formatInrWhole(lastBill.grand_total)}. Please find the invoice PDF attached.`
    : '';

  return (
    <div className="admin-billing">
      <AdminNav />

      <header className="bill-hero">
        <h1>Billing</h1>
        <p className="bill-hero-sub">
          Create GST bills with PDF, store transactions, and message customers on WhatsApp (attach the PDF after download).
        </p>
      </header>

      <div className="bill-tabs" role="tablist">
        {[
          { id: 'new', label: 'New bill' },
          { id: 'transactions', label: 'Transactions' },
          { id: 'settings', label: 'Invoice settings' },
        ].map((t) => (
          <button
            key={t.id}
            type="button"
            role="tab"
            className={`bill-tab ${tab === t.id ? 'is-active' : ''}`}
            onClick={() => setTab(t.id)}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'settings' && (
        <div className="bill-panel">
          <h2>Shop details on PDF</h2>
          {settingsLoading ? (
            <p>Loading…</p>
          ) : (
            <form onSubmit={saveSettings} className="bill-form-grid">
              <label>
                Shop name
                <input value={shopName} onChange={(e) => setShopName(e.target.value)} required />
              </label>
              <label>
                Address
                <textarea value={shopAddress} onChange={(e) => setShopAddress(e.target.value)} rows={3} />
              </label>
              <label>
                GSTIN
                <input value={shopGstin} onChange={(e) => setShopGstin(e.target.value)} />
              </label>
              <label>
                Shop phone
                <input value={shopPhone} onChange={(e) => setShopPhone(e.target.value)} />
              </label>
              <label>
                Default GST % (on subtotal)
                <input
                  type="number"
                  min="0"
                  max="100"
                  step="0.01"
                  value={defaultGst}
                  onChange={(e) => setDefaultGst(e.target.value)}
                />
              </label>
              <label>
                Logo (optional)
                <input
                  type="file"
                  accept="image/jpeg,image/png,image/webp,image/gif"
                  onChange={(e) => {
                    setLogoFile(e.target.files?.[0] || null);
                    setClearLogo(false);
                  }}
                />
              </label>
              {hasLogo && (
                <div>
                  {!clearLogo && (
                    <img
                      className="bill-logo-preview"
                      src={`/api/invoice-settings/logo?bust=${logoBust}`}
                      alt="Current logo"
                    />
                  )}
                  <label className="bill-checkbox" style={{ marginTop: '0.5rem' }}>
                    <input
                      type="checkbox"
                      checked={clearLogo}
                      onChange={(e) => setClearLogo(e.target.checked)}
                    />
                    Remove logo from PDF
                  </label>
                </div>
              )}
              <div className="bill-btn-row">
                <button type="submit" className="bill-btn bill-btn--primary" disabled={settingsSaving}>
                  {settingsSaving ? 'Saving…' : 'Save settings'}
                </button>
              </div>
            </form>
          )}
        </div>
      )}

      {tab === 'new' && (
        <div className="bill-panel">
          <h2>Create bill</h2>
          <form onSubmit={submitBill}>
            <input
              className="bill-search"
              type="search"
              placeholder="Search available products…"
              value={jewelQuery}
              onChange={(e) => setJewelQuery(e.target.value)}
            />
            {jewelsLoading ? (
              <p>Loading catalog…</p>
            ) : (
              <div className="bill-jewel-list">
                {availableJewels.length === 0 ? (
                  <p className="bill-error" style={{ padding: '0.75rem' }}>
                    No matching unsold products.
                  </p>
                ) : (
                  availableJewels.slice(0, 80).map((j) => (
                    <div key={j.id} className="bill-jewel-row">
                      <div>
                        <strong>{j.name}</strong>
                        <div className="bill-jewel-meta">
                          #{j.id} · {j.material} · {formatInrWhole(j.display_price)}
                        </div>
                      </div>
                      <button type="button" className="bill-btn bill-btn--secondary" onClick={() => addJewel(j.id)}>
                        Add
                      </button>
                    </div>
                  ))
                )}
              </div>
            )}

            <h3 style={{ fontSize: '1rem', margin: '1rem 0 0.5rem' }}>Line items</h3>
            <div className="bill-lines">
              {lineEntries.length === 0 ? (
                <p style={{ color: '#666', fontSize: '0.9rem' }}>No items yet — use Add above.</p>
              ) : (
                lineEntries.map(([idStr]) => {
                  const j = jewels.find((x) => String(x.id) === idStr);
                  const qty = quantities[idStr];
                  return (
                    <div key={idStr} className="bill-line">
                      <span style={{ flex: 1, minWidth: '10rem' }}>{j ? j.name : `Jewel #${idStr}`}</span>
                      <label>
                        Qty
                        <input
                          type="number"
                          min="1"
                          value={qty}
                          onChange={(e) => setQty(idStr, e.target.value)}
                        />
                      </label>
                      <span>{formatInrWhole((j?.display_price != null ? Math.round(Number(j.display_price)) : 0) * qty)}</span>
                      <button type="button" className="bill-btn bill-btn--secondary" onClick={() => removeLine(idStr)}>
                        Remove
                      </button>
                    </div>
                  );
                })
              )}
            </div>

            <div className="bill-form-grid" style={{ marginTop: '1rem' }}>
              <label>
                Customer name *
                <input value={customerName} onChange={(e) => setCustomerName(e.target.value)} required />
              </label>
              <label>
                Customer address
                <textarea value={customerAddress} onChange={(e) => setCustomerAddress(e.target.value)} rows={2} />
              </label>
              <label>
                Customer phone *
                <input value={customerPhone} onChange={(e) => setCustomerPhone(e.target.value)} required />
              </label>
              <label>
                GST % (this bill)
                <input
                  type="number"
                  min="0"
                  max="100"
                  step="0.01"
                  value={gstPercent}
                  onChange={(e) => setGstPercent(e.target.value)}
                />
              </label>
              <label className="bill-checkbox">
                <input type="checkbox" checked={markSold} onChange={(e) => setMarkSold(e.target.checked)} />
                Mark billed products as sold
              </label>
            </div>

            <div className="bill-summary">
              <div>Subtotal: {formatInrWhole(previewTotals.subtotal)}</div>
              <div>GST: {formatInrWhole(previewTotals.gst)}</div>
              <div>
                <strong>Grand total: {formatInrWhole(previewTotals.grand)}</strong>
              </div>
            </div>

            {billError ? <p className="bill-error">{billError}</p> : null}

            <div className="bill-btn-row">
              <button type="submit" className="bill-btn bill-btn--primary" disabled={billSubmitting}>
                {billSubmitting ? 'Creating…' : 'Create bill & PDF'}
              </button>
            </div>
          </form>

          {lastBill && (
            <div className="bill-success">
              <strong>Bill {lastBill.bill_number}</strong> created — total {formatInrWhole(lastBill.grand_total)}.
              <div className="bill-success-actions">
                <button
                  type="button"
                  className="bill-btn bill-btn--secondary"
                  onClick={() => downloadBillPdf(lastBill.id, lastBill.bill_number)}
                >
                  Download PDF
                </button>
                <a
                  className="bill-btn--wa"
                  href={whatsappUrl(lastBill.customer_phone, waText)}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <WhatsAppIcon />
                  WhatsApp customer
                </a>
              </div>
            </div>
          )}
        </div>
      )}

      {tab === 'transactions' && (
        <div className="bill-panel">
          <h2>Transactions</h2>
          <div className="bill-toolbar">
            <span style={{ fontSize: '0.72rem', fontWeight: 700, textTransform: 'uppercase', color: '#6b5b3d' }}>
              Period
            </span>
            {['this_month', 'last_month', 'custom'].map((p) => (
              <button
                key={p}
                type="button"
                className={`bill-tab ${period === p ? 'is-active' : ''}`}
                onClick={() => {
                  setListPage(1);
                  setPeriod(p);
                }}
              >
                {p === 'this_month' ? 'This month' : p === 'last_month' ? 'Last month' : 'Custom'}
              </button>
            ))}
            {period === 'custom' && (
              <div className="custom-range">
                <input type="date" value={customFrom} onChange={(e) => setCustomFrom(e.target.value)} />
                <span>to</span>
                <input type="date" value={customTo} onChange={(e) => setCustomTo(e.target.value)} />
                <button
                  type="button"
                  className="bill-btn bill-btn--secondary"
                  onClick={() => {
                    setListPage(1);
                    fetchTransactions();
                    fetchStats();
                  }}
                >
                  Apply
                </button>
              </div>
            )}
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.75rem', marginBottom: '1rem' }}>
            <input
              className="bill-search"
              style={{ maxWidth: '12rem', margin: 0 }}
              placeholder="Phone filter"
              value={filterPhone}
              onChange={(e) => setFilterPhone(e.target.value)}
            />
            <input
              className="bill-search"
              style={{ maxWidth: '12rem', margin: 0 }}
              placeholder="Customer name"
              value={filterName}
              onChange={(e) => setFilterName(e.target.value)}
            />
            <button
              type="button"
              className="bill-btn bill-btn--secondary"
              onClick={() => {
                setListPage(1);
                fetchTransactions();
              }}
            >
              Search list
            </button>
          </div>
          <div className="bill-stats-strip">
            <div>
              Bills
              <strong>{stats.bill_count}</strong>
            </div>
            <div>
              Total sales ({period === 'custom' ? 'range' : period.replace('_', ' ')})
              <strong>{formatInrWhole(stats.total_sales)}</strong>
            </div>
          </div>
          {listLoading ? (
            <p>Loading…</p>
          ) : bills.length === 0 ? (
            <p>No bills in this period.</p>
          ) : (
            <>
              <div className="bill-table-wrap">
                <table className="bill-table">
                  <thead>
                    <tr>
                      <th>Bill</th>
                      <th>Customer</th>
                      <th>Phone</th>
                      <th>Total</th>
                      <th>Date</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {bills.map((b) => (
                      <tr key={b.id}>
                        <td>{b.bill_number}</td>
                        <td>{b.customer_name}</td>
                        <td>{b.customer_phone}</td>
                        <td>{formatInrWhole(b.grand_total)}</td>
                        <td>{new Date(b.created_at).toLocaleString('en-IN')}</td>
                        <td>
                          <button
                            type="button"
                            className="bill-btn bill-btn--secondary"
                            style={{ padding: '0.35rem 0.65rem', fontSize: '0.8rem' }}
                            onClick={() => downloadBillPdf(b.id, b.bill_number)}
                          >
                            PDF
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {totalPages > 1 && (
                <div className="bill-pagination">
                  <button type="button" disabled={listPage <= 1} onClick={() => setListPage((p) => p - 1)}>
                    Previous
                  </button>
                  <span>
                    Page {listPage} of {totalPages}
                  </span>
                  <button
                    type="button"
                    disabled={listPage >= totalPages}
                    onClick={() => setListPage((p) => p + 1)}
                  >
                    Next
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}
